import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { jwtDecode } from 'jwt-decode'
import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useLocation, useNavigate, useSearchParams, Link } from 'react-router'
import z from 'zod'

import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { PasswordInput } from '@/components/PasswordInput'
import {
  claimInvitation,
  getUser,
  resetPassword,
  validateResetToken,
} from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const PasswordReset = observer(() => {
  // Context
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, setTokens, setUser, setUserId, user, userId } =
    useContext(UserStoreContext)

  const token = searchParams.get('token')
  const newUser = location.pathname === '/set-password'
  const navigate = useNavigate()

  // Whether the visitor was signed in when they landed. Arriving signed in on
  // an invitation link means "link this invitation to my account" (RSCX-351);
  // becoming signed in mid-visit is the normal post-set-password login, which
  // should proceed to the dashboard (PublicRoute no longer bounces this page
  // when a token is present, so the redirect lives here).
  const [arrivedAuthenticated] = useState(isAuthenticated)
  const claimMode = arrivedAuthenticated && newUser && Boolean(token)

  // State
  const [loading, setLoading] = useState(false)
  const [redirect, setRedirect] = useState(false)
  const [staticError, setStaticError] = useState(null)
  const [claiming, setClaiming] = useState(false)

  /**
   * Links the invitation to the signed-in account: the shell account created
   * by the invite is folded in and the invited address becomes a verified
   * login for this account.
   */
  const handleClaim = async () => {
    setClaiming(true)
    try {
      const result = await claimInvitation(token)
      toast(`Invitation linked — ${result.email} was added to your account`, 'success')
      navigate('/accounts')
    } catch (err) {
      toast(err.message, 'error')
      setClaiming(false)
    }
  }

  // Data Loading
  const handleError = (m) => toast(m, 'error')
  const { error, data: updatedUser } = useQuery({
    queryKey: ['users', userId],
    queryFn: () => getUser(userId),
    enabled: !!userId,
  })

  useEffect(() => {
    if (token && !isAuthenticated) {
      validateResetToken(token, (err) => {
        if (err.includes('expired') && newUser) {
          setStaticError(err)
        } else if (err.includes('expired')) {
          handleError('Reset token expired. Please request a new one.')
          setRedirect('/password-reset-request')
        } else {
          handleError(err)
        }
      })
    }
  }, [isAuthenticated, newUser, token])

  useEffect(() => {
    setLoading(false)

    if (updatedUser) {
      setUser(updatedUser)
    } else if (error) {
      handleError(error)
    }
  }, [error, setUser, updatedUser])

  // Schema depends on whether this is a new user or password reset
  const schema = newUser
    ? z
        .object({
          firstName: z.string().min(1, 'First name is required'),
          lastName: z.string().min(1, 'Last name is required'),
          password: z.string().min(8, 'Password must be at least 8 characters long'),
          confirmPassword: z.string(),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: 'Passwords do not match',
          path: ['confirmPassword'],
        })
    : z
        .object({
          password: z.string().min(8, 'Password must be at least 8 characters long'),
          confirmPassword: z.string(),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: 'Passwords do not match',
          path: ['confirmPassword'],
        })

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(schema),
  })

  /**
   * Helper to set the redirect parameters based on whether or not the user is
   * setting or RE-setting a password
   */
  const getRedirectParams = () => {
    if (newUser) return '?created=true'
    if (redirect === '/password-reset-request') return '?tokenExpired=true'
    return '?reset=true'
  }

  /**
   * Handles form submission
   * - Logs the user in if successful (via setting tokens in the store)
   * @param {object} data
   */
  const onSubmit = async (data) => {
    setLoading(true)
    const response = await resetPassword({ ...data, token }, handleError)

    if (response) {
      const decoded = jwtDecode(response.access)
      setTokens(response)
      setUserId(decoded.user_id)
    } else {
      setLoading(false)
    }
  }

  return (
    <>
      {redirect && <Navigate to={{ pathname: '/login', search: getRedirectParams() }} />}
      {isAuthenticated && !arrivedAuthenticated && <Navigate to={{ pathname: '/accounts' }} />}

      <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-sm md:max-w-lg">
          <div className="flex flex-col gap-6">
            <Card className="overflow-hidden p-0">
              <CardContent className="p-0">
                {(() => {
                  if (claimMode) {
                    return (
                      <div className="flex w-full flex-col items-center gap-4 p-8 text-center">
                        <h1>Link this invitation</h1>
                        <p className="text-muted-foreground text-sm text-balance">
                          You&apos;re signed in as{' '}
                          <span className="text-foreground font-medium">{user?.email}</span>. Link
                          this invitation to your account and the invited address becomes another
                          way to sign in — no separate account needed.
                        </p>
                        <Button onClick={handleClaim} loading={claiming}>
                          Link Invitation
                        </Button>
                        <Link
                          to="/accounts"
                          className="text-muted-foreground text-sm underline-offset-2 hover:underline"
                        >
                          Not now — go to my dashboard
                        </Link>
                      </div>
                    )
                  }
                  if (staticError) {
                    return (
                      <div className="flex w-full flex-col items-center gap-6 space-y-2 p-8">
                        <h3 className="text-destructive">{staticError}</h3>
                        <Link
                          to="/login"
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          Back to Login
                        </Link>
                      </div>
                    )
                  }
                  return (
                    <Form {...form}>
                      <form className="p-6 md:p-8" onSubmit={form.handleSubmit(onSubmit)}>
                        <div className="flex flex-col gap-6">
                          <div className="flex flex-col items-center text-center">
                            <h1>{newUser ? 'Set your password' : 'Reset your password'}</h1>
                            <p className="text-muted-foreground mt-2 text-center text-sm text-balance">
                              Or{' '}
                              <Link
                                to="/login"
                                className="text-primary ml-auto underline-offset-2 hover:underline"
                              >
                                Log In
                              </Link>
                            </p>
                            {newUser && token && (
                              <p className="text-muted-foreground mt-2 text-center text-sm text-balance">
                                Already have a RescueX account?{' '}
                                <Link
                                  to="/login"
                                  onClick={() =>
                                    sessionStorage.setItem('claimInvitationToken', token)
                                  }
                                  className="text-primary underline-offset-2 hover:underline"
                                >
                                  Sign in to link this invitation
                                </Link>{' '}
                                instead of creating a new one.
                              </p>
                            )}
                          </div>

                          {newUser && (
                            <>
                              <FormField
                                control={form.control}
                                disabled={loading}
                                name="firstName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>First Name</FormLabel>
                                    <FormControl>
                                      <Input
                                        data-testid="firstName"
                                        id="firstName"
                                        autoComplete="given-name"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                                rules={{ required: true }}
                              />
                              <FormField
                                control={form.control}
                                disabled={loading}
                                name="lastName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Last Name</FormLabel>
                                    <FormControl>
                                      <Input
                                        data-testid="lastName"
                                        id="lastName"
                                        autoComplete="family-name"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                                rules={{ required: true }}
                              />
                            </>
                          )}
                          <FormField
                            control={form.control}
                            disabled={loading}
                            name="password"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Password</FormLabel>
                                <FormControl>
                                  <PasswordInput data-testid="password" id="password" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                            rules={{ required: true }}
                          />
                          <FormField
                            control={form.control}
                            disabled={loading}
                            name="confirmPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Confirm Password</FormLabel>
                                <FormControl>
                                  <PasswordInput
                                    data-testid="confirmPassword"
                                    id="confirmPassword"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <Button loading={loading} type="submit" className="w-full">
                            Submit
                          </Button>
                        </div>
                      </form>
                    </Form>
                  )
                })()}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
})

export default PasswordReset
